import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { GraduationCap, ExternalLink, FileDown, Clock } from "lucide-react";
import RegulatoryBadges from "@/components/RegulatoryBadges";

const SIZE_ASPECT = { compact: "aspect-[21/9]", standard: "aspect-[16/9]", wide: "aspect-[3/2]" };

export default function Trainings() {
  const { t, lang, pick } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/trainings").then((r) => setItems(r.data || [])).catch(() => {}); }, []);

  return (
    <div data-testid="trainings-page">
      <section className="bg-navy text-cream py-24">
        <div className="container-x">
          <span className="eyebrow">{t("nav.training")}</span>
          <h1 className="mt-4 text-4xl md:text-6xl">
            {lang === "ar" ? "برامج التدريب والتطوير" : "Training & Development"}
          </h1>
          <span className="divider-gold" />
          <p className="text-cream/80 max-w-2xl leading-relaxed">
            {lang === "ar"
              ? "برامج مركّزة صممناها لبناء ثقافة الحوكمة والامتثال داخل مؤسستك."
              : "Focused programmes crafted to build governance and compliance culture inside your institution."}
          </p>
        </div>
      </section>

      {items.length === 0 ? (
        <section className="container-x py-24 text-center text-navy/50" data-testid="trainings-empty">
          {lang === "ar" ? "لا توجد برامج تدريبية منشورة حالياً." : "No training programmes published yet."}
        </section>
      ) : (
        <section className="container-x py-24 grid grid-cols-1 md:grid-cols-2 gap-8">
          {items.map((it) => {
            const showImg = it.show_image !== false && !!it.image_url;
            const aspect = SIZE_ASPECT[it.image_size] || SIZE_ASPECT.standard;
            const soon = !!it.is_coming_soon;
            return (
              <article key={it.id} data-testid={`training-${it.id}`}
                       className="card-elev overflow-hidden hover:-translate-y-1 transition-transform relative">
                {soon && (
                  <span className="absolute top-3 right-3 z-10 chip !bg-amber-400 !text-navy !border-amber-500 font-bold"
                        data-testid={`training-soon-${it.id}`}>
                    <Clock size={11} /> {lang === "ar" ? "قريباً" : "Coming Soon"}
                  </span>
                )}
                {showImg && (
                  <div className={`${aspect} overflow-hidden max-h-[420px]`}>
                    <img src={it.image_url} alt={pick(it, "title")}
                         className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="p-8 md:p-10">
                  <h2 className="text-2xl md:text-3xl text-navy">{pick(it, "title")}</h2>
                  <RegulatoryBadges value={it.regulatory_frameworks} lang={lang} testId={`training-reg-${it.id}`} />
                  <span className="divider-gold" />
                  <p className="text-navy/75 leading-relaxed">{pick(it, "description")}</p>
                  {!soon && it.show_button && (() => {
                    // T-01.4: raw Training/Materials URLs no longer ship in
                    // /data/trainings.json. Every course card routes trainees
                    // through the gated enrollment page — /training/<slug> —
                    // regardless of access_type. If a course has no slug yet
                    // (legacy row), we render no CTA to avoid a broken link.
                    if (!it.slug) return null;
                    const label = lang === "ar" ? "سجّل الآن" : "Enroll now";
                    return (
                      <div className="mt-8 flex flex-wrap gap-3" data-testid={`training-cta-${it.id}`}>
                        <a href={`/training/${it.slug}`}
                           data-testid={`training-enroll-${it.id}`}
                           className="inline-flex items-center gap-2 px-5 py-3 bg-gold text-navy text-sm font-semibold rounded-sm hover:brightness-110">
                          {label} <ExternalLink size={14} />
                        </a>
                      </div>
                    );
                  })()}
                </div>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}
